import { Badge, MUICheckBox } from "@abb/arcadia-ui-react"
import { MouseEvent, useCallback, useMemo, useState } from "react"
import { useDeleteConfirm } from "../../../../hooks/useDeleteConfirm"
import { useNotifications } from "../../../../hooks/useNotifications"
import { DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { NotificationGet } from "../../../models/Notification"
import { deleteEntity } from "../../../utils/deleteEntity"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../utils/getFormattedDate"
import { getNotificationStatusProps } from "../../../utils/getNotificationStatusProps"
import { DiMaAddButton } from "../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

export function Notifications() {
  const [getExpired, setGetExpired] = useState<boolean>(false)
  const { notificationsList, isLoadingNotifications } = useNotifications(getExpired)
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const cols = [
    {
      field: "title",
      headerName: "Title",
      isLink: true,
      linkBasePath: "/notifications",
    },
    { field: "applicationName", headerName: "App" },
    {
      field: "validityStartDate",
      headerName: "Validity Start Date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.validityStartDate)),
      sortComparator: dateStringComparator,
    },
    {
      field: "validityEndDate",
      headerName: "Validity End Date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.validityEndDate)),
      sortComparator: dateStringComparator,
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: (row: EntityRow) => (
        <Badge
          {...getNotificationStatusProps(
            row.status as "active" | "expired" | "planned"
          )}
        />
      ),
    },
  ]

  const rows: EntityRow[] = notificationsList.map((notification: NotificationGet) => ({
    id: notification.id,
    title: notification.title,
    applicationName: notification.application.name,
    validityStartDate: String(notification.validityStartDate),
    validityEndDate: String(notification.validityEndDate),
    status: notification.status,
  }))

  const onChange = useCallback(() => {
    setGetExpired((exp) => !exp)
  }, [])
  const handleIncludeExpiredToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      onChange()
    },
    [onChange]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() => deleteEntity(`/notifications/${row.id}`))
      }
    },
    [requestDelete]
  )

  const toolbar = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          width: "100%",
        }}
      >
        <MUICheckBox
          checked={getExpired}
          label="Include expired notifications"
          size={16}
          handleToggle={handleIncludeExpiredToggle}
        />

        <DiMaAddButton
          url="/notifications/create"
          feature={DimaFeatureShortNameEnum.NM}
        />
      </div>
    ),
    [getExpired, handleIncludeExpiredToggle]
  )

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={cols}
        isLoading={isLoadingNotifications}
        toolbar={toolbar}
        actionItems={[{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]}
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
